import { getStorageItem } from "@exxatdesignux/ui/lib/persisted-state"

import { DEFAULT_EXAM_CATEGORIES } from "./defaults"
import { EXAM_CATEGORIES_STORE_KEY, readExamCategoryStore } from "./storage"
import { findCategory, getRootCategories, getRootCategoryId } from "./tree"
import type { ExamCategory } from "./types"

/** Older product labels that still need to resolve to a Content category. */
const CATEGORY_ALIASES: Record<string, string> = {
  "infectious disease": "infectious-disease",
  cardiology: "cardiology",
  "cardiovascular system": "cardiology",
  "cardiovascular conditions": "cardiology",
  pulmonology: "pulmonology",
  "pulmonary conditions": "pulmonology",
  pharmacology: "pharmacology",
  "pharmacology and therapeutics": "pharmacology",
  "clinical reasoning": "diagnostic-reasoning",
  pediatrics: "pediatrics",
  "pediatric conditions": "pediatrics",
  endocrinology: "endocrinology",
  "endocrine conditions": "endocrinology",
  neurology: "neurology",
  "neurologic conditions": "neurology",
}

export function loadContentCategories(): ExamCategory[] {
  return readExamCategoryStore(getStorageItem(EXAM_CATEGORIES_STORE_KEY))
}

export function resolveContentCategory(
  idOrName: string,
  categories: ExamCategory[] = loadContentCategories(),
): ExamCategory | undefined {
  const trimmed = idOrName.trim()
  if (!trimmed) return undefined

  const byId = findCategory(categories, trimmed)
  if (byId) return byId

  const lower = trimmed.toLowerCase()
  const byIdInsensitive = findCategory(categories, lower)
  if (byIdInsensitive) return byIdInsensitive

  const aliasedId = CATEGORY_ALIASES[lower]
  if (aliasedId) {
    const aliased = findCategory(categories, aliasedId)
    if (aliased) return aliased
  }

  return categories.find((category) => category.name.toLowerCase() === lower)
}

export function labelForContentCategory(
  idOrName: string,
  categories: ExamCategory[] = loadContentCategories(),
): string {
  return resolveContentCategory(idOrName, categories)?.name ?? idOrName
}

export function labelsForContentCategories(
  idsOrNames: readonly string[],
  categories: ExamCategory[] = loadContentCategories(),
): string[] {
  return idsOrNames.map((value) => labelForContentCategory(value, categories))
}

/** Keep only the selected Content category domains and their descendants. */
export function contentCategoriesForDomains(
  domainKeys: readonly string[],
  categories: ExamCategory[] = loadContentCategories(),
): ExamCategory[] {
  const allowedRoots = new Set(
    domainKeys
      .map((key) => resolveContentCategory(key, categories))
      .filter((category): category is ExamCategory => Boolean(category))
      .map((category) => getRootCategoryId(categories, category.id) ?? category.id),
  )
  if (allowedRoots.size === 0) return []
  return categories.filter((category) => {
    const rootId = getRootCategoryId(categories, category.id)
    return rootId != null && allowedRoots.has(rootId)
  })
}

export function contentCategoryDomainNames(
  categories: ExamCategory[] = loadContentCategories(),
): string[] {
  return getRootCategories(categories).map((category) => category.name)
}
